/**
 * Dollars↔cents helpers for money-input forms. Storage shape on the
 * backend is integer cents (CLAUDE.md money rule); these convert at
 * the form edge so the UI can render `<input type="number" step="0.01">`
 * with two-decimal dollar values.
 *
 * `dollarsToCents` rounds rather than truncates so `.005` lands on the
 * nearest cent (avoids the off-by-one floor when float multiplication
 * produces 12.99999998 instead of 13.0).
 */

export function dollarsToCents(value: string | number): number {
  const dollars = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

export function centsToDollars(cents: number): string {
  if (!Number.isFinite(cents)) return "0.00";
  return (cents / 100).toFixed(2);
}

/**
 * Pretty-print cents as a USD currency string, with sign + thousands
 * separators. Used in the journal P&L cell.
 */
export function formatCentsCurrency(cents: number): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  const fraction = (abs % 100).toString().padStart(2, "0");
  const wholeFmt = whole.toLocaleString("en-US");
  return `${negative ? "-" : ""}$${wholeFmt}.${fraction}`;
}

/**
 * Pretty-print cents as compact USD without sub-dollar precision (drops
 * the `.00`). Used by the dashboard headline + chart axis.
 *
 * `hidden` is the privacy-mode escape hatch: returns `••••` so the
 * dashboard can mask all numeric content with a single toggle.
 */
export function formatCentsCompact(
  cents: number,
  opts?: { readonly hidden?: boolean },
): string {
  if (opts?.hidden === true) return "••••";
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.trunc(abs / 100);
  return `${negative ? "-" : ""}$${whole.toLocaleString("en-US")}`;
}
